import React, { useEffect } from 'react';
import { ScopedCssBaseline, ThemeProvider } from '@mui/material';

import { CoreBox, CoreTypography } from '@youscience/core';
import { brightpathTheme } from '@youscience/theme';
import { Roles } from '@youscience/commonapp-service-common';

import useCommonAppAdminStore from '@store/CommonAppAdminStore';
import useRecommenderStore from '@store/RecommenderStore';

import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';
import { ROUTES } from '@constants/ROUTES';

import { useUserProfile } from '@hooks/useUserProfile';
import { useSaveProfile } from './useProfile';

import { Breadcrumbs } from '@components/Breadcrumbs';
import SaveChangesModal from '@components/SaveChangesModal';

import PersonalDetails from './components/PersonalDetails';
import Certifications from './components/Certifications';
import SchoolDetails from './components/SchoolDetails';
import ProfileButtons from '../ProfileButtons';

import { StyledHero, StyledHeadline, StyledHeading, StyledButtonContainer } from './RecommenderProfile.styles';
import { CounselorSection, counselorSections, PersonalDetailsSection, TeacherSection, teacherSections } from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const counselorComponents: { [key in CounselorSection]: React.ComponentType<any> } = {
  [CounselorSection.Certifications]: Certifications,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const teacherComponents: { [key in TeacherSection]: React.ComponentType<any> } = {
  [TeacherSection.SchoolDetailsTeacher]: SchoolDetails,
};

export const RecommenderProfile = () => {
  const { getRoles, userRoles } = useCommonAppAdminStore((store) => store);

  const isCounselorRole = userRoles?.includes(Roles.COUNSELOR);
  const isTeacherRole = userRoles?.includes(Roles.TEACHER);

  const { getRecommenderProfile, recommenderProfile } = useRecommenderStore((store) => store);

  useEffect(() => {
    const fetchData = async () => {
      await getRoles();
      await getRecommenderProfile();
    };

    void fetchData();
  }, []);

  const { firstName, lastName } = useUserProfile();
  const { sectionState, toggleExpandedSelection, saveAndContinue, saveProfile, allFormsAreValid, hasChanges } =
    useSaveProfile();

  return (
    <ThemeProvider theme={brightpathTheme}>
      <ScopedCssBaseline />

      <StyledHero>
        <Breadcrumbs
          links={[
            { to: `${ROUTES.RECOMMENDER_SPACE}${location.hash}`, text: BREADCRUMBS_PAGE_TEXT.RECOMMENDER_WORKSPACE },
          ]}
          currentPage={BREADCRUMBS_PAGE_TEXT.RECOMMENDER_PROFILE}
        />

        <CoreTypography variant="overline">
          {recommenderProfile?.highSchool
            ? `${recommenderProfile.highSchool.schoolName}, CEEB CODE: ${recommenderProfile.highSchool.schoolCode}`
            : null}
        </CoreTypography>

        <StyledHeadline>
          <CoreTypography sx={{ fontSize: '36px', marginBottom: '0.5rem' }} variant="h3">
            {firstName} {lastName}
          </CoreTypography>
        </StyledHeadline>
      </StyledHero>

      <StyledHeading>
        <CoreBox>
          <CoreBox sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem' }}>
            <CoreBox sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <CoreTypography variant="h4">Recommender profile</CoreTypography>

              <CoreTypography>We send your profile information with all your forms.</CoreTypography>
            </CoreBox>

            <ProfileButtons
              onSave={saveProfile}
              isValid={allFormsAreValid}
              showUpdateForAllStudents={false}
              hasChanges={hasChanges ?? false}
            />
          </CoreBox>
        </CoreBox>

        <PersonalDetails
          id={PersonalDetailsSection.PersonalDetails}
          isExtended={sectionState.expandedSection === PersonalDetailsSection.PersonalDetails}
          isSaved={sectionState.savedSections[PersonalDetailsSection.PersonalDetails]}
          onExpand={() => toggleExpandedSelection(PersonalDetailsSection.PersonalDetails)}
          onSaveAndContinue={() => saveAndContinue(PersonalDetailsSection.PersonalDetails)}
        />

        {isCounselorRole &&
          counselorSections.map((section) => {
            const Component = counselorComponents[section];

            return (
              <Component
                key={section}
                id={section}
                isExtended={sectionState.expandedSection === section}
                isSaved={sectionState.savedSections[section]}
                onExpand={() => toggleExpandedSelection(section)}
                onSaveAndContinue={() => saveAndContinue(section)}
                onSave={saveProfile}
              />
            );
          })}
        {isTeacherRole &&
          teacherSections.map((section) => {
            const Component = teacherComponents[section];

            return (
              <Component
                key={section}
                id={section}
                isExtended={sectionState.expandedSection === section}
                isSaved={sectionState.savedSections[section]}
                onExpand={() => toggleExpandedSelection(section)}
                onSaveAndContinue={() => saveAndContinue(section)}
              />
            );
          })}

        <StyledButtonContainer>
          <ProfileButtons
            onSave={saveProfile}
            isValid={allFormsAreValid}
            showUpdateForAllStudents={false}
            hasChanges={hasChanges ?? false}
          />
        </StyledButtonContainer>
      </StyledHeading>

      <SaveChangesModal hasUnsavedChanges={hasChanges ?? false} onSaveChanges={saveProfile} />
    </ThemeProvider>
  );
};
